import type { ModuleKey } from '../session/store/useSessionStore';
import type { BusinessCapability } from '../session/domain/businessProfile';
import {
  moduleAllowedByCapabilities,
  navItemAllowedByCapabilities,
} from '../feature-flags/businessCapabilitiesService';
import type { Ionicons } from '@expo/vector-icons';
import type { NavItemId } from './navItemId';
export type { NavItemId } from './navItemId';

/** Zonas de la IA de navegación (Vuelto Negocio). */
export type NavZone =
  | 'sell'
  | 'control'
  | 'inventory'
  | 'pricing'
  | 'storefront'
  | 'negocio'
  | 'account';

export type NavSurface = 'mobileTab' | 'mobileMore' | 'sidebar' | 'stackOnly';

export type NavigationManifestItem = {
  id: NavItemId;
  /** Ruta React Navigation (tab name o stack screen). */
  route: string;
  label: string;
  /** Etiqueta corta para el header móvil (si omita, usa `label`). */
  shortLabel?: string;
  zone: NavZone;
  zoneLabel: string;
  module?: ModuleKey;
  /** Flag global (`globalFeatureFlags`) o plan; si falta, siempre visible con permiso. */
  featureFlag?: 'fiado_integrations' | 'promotions_integrations' | 'price_lists_integrations' | 'loyalty_integrations' | 'expenses_module' | 'public_store_features' | 'ai_chat_enabled';
  surfaces: NavSurface[];
  icon: keyof typeof Ionicons.glyphMap;
  /** Orden dentro de la zona / barra. */
  order: number;
};

export { WEB_SIDEBAR_BREAKPOINT, APP_SIDEBAR_WIDTH } from '../../shared/layout/breakpoints';

export const NAV_ZONE_LABELS: Record<NavZone, string> = {
  sell: 'Vender',
  control: 'Administrar',
  inventory: 'Inventario',
  pricing: 'Precios',
  storefront: 'Tienda online',
  negocio: 'Negocio',
  account: 'Cuenta y ayuda',
};

/**
 * Manifiesto: fuente única para tabs mobile, hub «Más», sidebar web y documentación.
 *
 * Tabs mobile (Inicio + 3 + Más): se resuelven en `getMobileTabManifestItems` (cascada).
 * Reordenar hub/sidebar: editar `order`, `zone` y `surfaces` aquí.
 */
export const NAVIGATION_MANIFEST: NavigationManifestItem[] = [
  {
    id: 'home',
    route: 'Home',
    label: 'Inicio',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    surfaces: ['mobileTab', 'sidebar'],
    icon: 'home-outline',
    order: 1,
  },
  {
    id: 'pos',
    route: 'POS',
    label: 'POS',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    module: 'POS',
    surfaces: ['sidebar'],
    icon: 'cart',
    order: 10,
  },
  {
    id: 'ordersInbox',
    route: 'OrdersInbox',
    label: 'Pedidos',
    shortLabel: 'Pedidos',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    // Multi-module hub (orders / storefront / agenda). Nav stays open when the
    // public-store flag is on; each segment is gated in OrdersInboxScreen.
    // Do not set module:'Orders' — that would hide Bandeja for agenda-only staff.
    featureFlag: 'public_store_features',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'bag-outline',
    order: 12,
  },
  {
    id: 'onlineOrders',
    route: 'OnlineOrders',
    label: 'Pedidos online',
    shortLabel: 'Pedidos',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    module: 'Orders',
    featureFlag: 'public_store_features',
    surfaces: ['stackOnly'],
    icon: 'bag-outline',
    order: 13,
  },
  {
    id: 'diningTables',
    route: 'DiningTables',
    label: 'Mesas',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    module: 'Tables',
    surfaces: ['sidebar', 'stackOnly', 'mobileMore'],
    icon: 'restaurant-outline',
    order: 31,
  },
  {
    id: 'diningReservations',
    route: 'Reservations',
    label: 'Reservas',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    module: 'Tables',
    surfaces: ['sidebar', 'stackOnly', 'mobileMore'],
    icon: 'calendar-outline',
    order: 32,
  },
  {
    id: 'kitchenQueue',
    route: 'KitchenQueue',
    label: 'Cocina',
    zone: 'sell',
    zoneLabel: NAV_ZONE_LABELS.sell,
    module: 'Kitchen',
    surfaces: ['sidebar', 'stackOnly', 'mobileMore'],
    icon: 'flame-outline',
    order: 34,
  },
  {
    id: 'menuOptionGroups',
    route: 'MenuOptionGroups',
    label: 'Opciones de menú',
    zone: 'inventory',
    zoneLabel: NAV_ZONE_LABELS.inventory,
    module: 'MenuOptions',
    // Absorbida por el hub Catálogo (Catalog → segmento «Opciones de menú»):
    // la ruta sigue registrada en el ShellStack para deep-links.
    surfaces: ['stackOnly'],
    icon: 'options-outline',
    order: 35,
  },
  {
    id: 'productCombos',
    route: 'ProductCombos',
    label: 'Combos',
    zone: 'inventory',
    zoneLabel: NAV_ZONE_LABELS.inventory,
    module: 'MenuOptions',
    // Absorbida por el hub Catálogo (Catalog → segmento «Combos»).
    surfaces: ['stackOnly'],
    icon: 'layers-outline',
    order: 36,
  },
  {
    id: 'salesList',
    route: 'SalesList',
    label: 'Ventas',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'POS',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'receipt-outline',
    order: 20,
  },
  {
    id: 'appointments',
    route: 'Appointments',
    label: 'Agenda',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Agenda',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'calendar-outline',
    order: 22,
  },
  {
    id: 'workOrders',
    route: 'WorkOrders',
    label: 'Órdenes de trabajo',
    shortLabel: 'OT',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'WorkOrders',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'construct-outline',
    order: 25,
  },
  {
    id: 'commissions',
    route: 'CommissionsDashboard',
    label: 'Comisiones',
    shortLabel: 'Comis.',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Commissions',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'cash-outline',
    order: 26,
  },
  {
    id: 'myCommissions',
    route: 'MyCommissions',
    label: 'Mis comisiones',
    shortLabel: 'Mis comis.',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Commissions',
    // Absorbida por el hub Comisiones (CommissionsDashboard): la ruta sigue
    // registrada como tab oculto para deep-links y navigate() directos.
    surfaces: ['stackOnly'],
    icon: 'wallet-outline',
    order: 27,
  },
  {
    id: 'commissionPlans',
    route: 'CommissionPlans',
    label: 'Planes de comisión',
    shortLabel: 'Planes',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Commissions',
    surfaces: ['stackOnly'],
    icon: 'settings-outline',
    order: 28,
  },
  {
    id: 'boothRentals',
    route: 'BoothRentals',
    label: 'Alquileres de cabina',
    shortLabel: 'Alquileres',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Commissions',
    surfaces: ['stackOnly'],
    icon: 'storefront-outline',
    order: 28.5,
  },
  {
    id: 'settlementDetail',
    route: 'SettlementDetail',
    label: 'Detalle liquidación',
    shortLabel: 'Liq.',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Commissions',
    // stackOnly: se abre desde el listado de liquidaciones. Antes surfaces: []
    // dejaba la ruta desregistrada (navigate('SettlementDetail') no resolvía).
    surfaces: ['stackOnly'],
    icon: 'document-text-outline',
    order: 29,
  },
  {
    id: 'cash',
    route: 'CashManagement',
    label: 'Caja',
    shortLabel: 'Caja',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Cash',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'cash-outline',
    order: 30,
  },
  {
    id: 'catalog',
    route: 'Catalog',
    label: 'Catálogo',
    zone: 'inventory',
    zoneLabel: NAV_ZONE_LABELS.inventory,
    module: 'Catalog',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'pricetags',
    order: 34,
  },
  {
    id: 'inventory',
    route: 'Inventory',
    label: 'Stock',
    zone: 'inventory',
    zoneLabel: NAV_ZONE_LABELS.inventory,
    module: 'Inventory',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'layers',
    order: 35,
  },
  {
    id: 'purchases',
    route: 'Purchases',
    label: 'Compras',
    zone: 'inventory',
    zoneLabel: NAV_ZONE_LABELS.inventory,
    module: 'Purchases',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'cart-outline',
    order: 36,
  },
  {
    id: 'expenses',
    route: 'Expenses',
    label: 'Gastos',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Expenses',
    featureFlag: 'expenses_module',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'wallet-outline',
    order: 38,
  },
  {
    id: 'fiado',
    route: 'Customers',
    label: 'Clientes',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Customers',
    featureFlag: 'fiado_integrations',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'people',
    order: 60,
  },
  {
    id: 'pricingHub',
    route: 'PricingHub',
    label: 'Promociones y precios',
    shortLabel: 'Precios',
    zone: 'pricing',
    zoneLabel: NAV_ZONE_LABELS.pricing,
    // Multi-module hub (Promotions / PriceLists / Loyalty): la puerta es visible
    // si al menos un hijo lo es — ver isItemVisible. Sin module/featureFlag propios.
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'pricetags-outline',
    order: 64,
  },
  {
    id: 'promotions',
    route: 'PromotionsList',
    label: 'Promociones',
    zone: 'pricing',
    zoneLabel: NAV_ZONE_LABELS.pricing,
    module: 'Promotions',
    featureFlag: 'promotions_integrations',
    // Absorbida por el hub Precios (PricingHub → segmento «Promociones»): la ruta
    // sigue registrada como tab oculto para deep-links y navigate() directos.
    surfaces: ['stackOnly'],
    icon: 'pricetag-outline',
    order: 65,
  },
  {
    id: 'priceLists',
    route: 'PriceListsList',
    label: 'Listas de precios',
    shortLabel: 'Listas',
    zone: 'pricing',
    zoneLabel: NAV_ZONE_LABELS.pricing,
    module: 'PriceLists',
    featureFlag: 'price_lists_integrations',
    surfaces: ['stackOnly'],
    icon: 'list-outline',
    order: 66,
  },
  {
    id: 'loyalty',
    route: 'LoyaltyProgram',
    label: 'Fidelización',
    shortLabel: 'Puntos',
    zone: 'pricing',
    zoneLabel: NAV_ZONE_LABELS.pricing,
    module: 'Loyalty',
    featureFlag: 'loyalty_integrations',
    surfaces: ['stackOnly'],
    icon: 'gift-outline',
    order: 67,
  },
  {
    id: 'reports',
    route: 'Reports',
    label: 'Informes',
    zone: 'control',
    zoneLabel: NAV_ZONE_LABELS.control,
    module: 'Reports',
    surfaces: ['mobileMore', 'sidebar'],
    icon: 'bar-chart',
    order: 70,
  },
  {
    id: 'onlineComplaints',
    route: 'OnlineComplaints',
    label: 'Reclamos online',
    shortLabel: 'Reclamos',
    zone: 'storefront',
    zoneLabel: NAV_ZONE_LABELS.storefront,
    module: 'BranchSettings',
    featureFlag: 'public_store_features',
    surfaces: ['stackOnly'],
    icon: 'chatbubbles-outline',
    order: 36,
  },
  {
    id: 'consultations',
    route: 'Consultations',
    label: 'Consultas de recetas',
    shortLabel: 'Consultas',
    zone: 'storefront',
    zoneLabel: NAV_ZONE_LABELS.storefront,
    module: 'BranchSettings',
    featureFlag: 'public_store_features',
    surfaces: ['stackOnly'],
    icon: 'medkit-outline',
    order: 37,
  },
  {
    id: 'quotes',
    route: 'Quotes',
    label: 'Cotizaciones',
    shortLabel: 'Cotizaciones',
    zone: 'storefront',
    zoneLabel: NAV_ZONE_LABELS.storefront,
    module: 'BranchSettings',
    featureFlag: 'public_store_features',
    surfaces: ['stackOnly'],
    icon: 'document-text-outline',
    order: 38,
  },
  {
    id: 'storefrontHub',
    route: 'StorefrontHub',
    label: 'Tienda online',
    zone: 'storefront',
    zoneLabel: NAV_ZONE_LABELS.storefront,
    module: 'BranchSettings',
    featureFlag: 'public_store_features',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'storefront-outline',
    order: 100,
  },
  {
    id: 'deliverySettings',
    route: 'DeliverySettings',
    label: 'Delivery y zonas',
    zone: 'storefront',
    zoneLabel: NAV_ZONE_LABELS.storefront,
    module: 'BranchSettings',
    featureFlag: 'public_store_features',
    // Absorbida por la puerta Tienda online (StorefrontHub → segmento
    // «Delivery y zonas»): la ruta sigue registrada para deep-links.
    surfaces: ['stackOnly'],
    icon: 'bicycle-outline',
    order: 105,
  },
  {
    id: 'storeReviews',
    route: 'StoreReviews',
    label: 'Reseñas',
    shortLabel: 'Reseñas',
    zone: 'storefront',
    zoneLabel: NAV_ZONE_LABELS.storefront,
    module: 'BranchSettings',
    featureFlag: 'public_store_features',
    // Absorbida por la puerta Tienda online (StorefrontHub → segmento
    // «Reseñas»): la ruta sigue registrada para deep-links.
    surfaces: ['stackOnly'],
    icon: 'star-outline',
    order: 107,
  },
  {
    id: 'settingsHub',
    route: 'SettingsHub',
    label: 'Configuración',
    shortLabel: 'Config.',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    // Hub multi-módulo (BranchSettings / Organization / Billing): la puerta es
    // visible si al menos un hijo lo es — ver isItemVisible.
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'settings-outline',
    order: 131,
  },
  {
    id: 'branchIdentity',
    route: 'BranchIdentity',
    label: 'Datos del local',
    shortLabel: 'Datos local',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'BranchSettings',
    // Absorbida por el hub Configuración (SettingsHub → fila «Este local»):
    // la ruta sigue registrada en el ShellStack para deep-links.
    surfaces: ['stackOnly'],
    icon: 'storefront-outline',
    order: 110,
  },
  {
    id: 'branchPosOps',
    route: 'BranchPosOps',
    label: 'Reglas del POS',
    shortLabel: 'Reglas POS',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'BranchSettings',
    surfaces: ['stackOnly'],
    icon: 'options-outline',
    order: 115,
  },
  {
    id: 'branchDevices',
    route: 'BranchDevices',
    label: 'Dispositivos',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'BranchSettings',
    surfaces: ['stackOnly'],
    icon: 'print-outline',
    order: 116,
  },
  {
    id: 'integrationsHub',
    route: 'IntegrationsHub',
    label: 'Integraciones',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'BranchSettings',
    surfaces: ['stackOnly'],
    icon: 'link-outline',
    order: 120,
  },
  {
    id: 'branchSettings',
    route: 'BranchSettings',
    label: 'Ajustes de sucursal',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'BranchSettings',
    surfaces: ['stackOnly'],
    icon: 'settings-outline',
    order: 125,
  },
  {
    id: 'team',
    route: 'Team',
    label: 'Equipo',
    shortLabel: 'Equipo',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'Team',
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'people-circle-outline',
    order: 130,
  },
  {
    id: 'organization',
    route: 'Organization',
    label: 'Organización',
    shortLabel: 'Org.',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'Organization',
    // Absorbida por el hub Configuración (SettingsHub → fila «Tu negocio»).
    surfaces: ['stackOnly'],
    icon: 'git-network-outline',
    order: 132,
  },
  {
    id: 'billing',
    route: 'Billing',
    label: 'Plan y facturación',
    shortLabel: 'Plan',
    zone: 'negocio',
    zoneLabel: NAV_ZONE_LABELS.negocio,
    module: 'Billing',
    // Solo si isBillingEnabled(); sigue disponible para deep links (stackOnly).
    surfaces: ['stackOnly'],
    icon: 'card-outline',
    order: 135,
  },
  {
    id: 'aiAssistant',
    route: 'AiChat',
    label: 'Asistente',
    shortLabel: 'Asistente',
    zone: 'account',
    zoneLabel: NAV_ZONE_LABELS.account,
    featureFlag: 'ai_chat_enabled',
    surfaces: ['sidebar', 'mobileMore', 'stackOnly'],
    icon: 'sparkles',
    order: 72,
  },
  {
    id: 'support',
    route: 'SupportReports',
    label: 'Soporte',
    zone: 'account',
    zoneLabel: NAV_ZONE_LABELS.account,
    // Always visible: own tickets. Branch scope via Support.view_branch_tickets.
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'headset-outline',
    order: 75,
  },
  {
    id: 'profile',
    route: 'Profile',
    label: 'Cuenta',
    zone: 'account',
    zoneLabel: NAV_ZONE_LABELS.account,
    surfaces: ['sidebar', 'mobileMore'],
    icon: 'person-circle-outline',
    order: 80,
  },
];

export type NavVisibilityContext = {
  allowedModules: Set<ModuleKey>;
  customerTabAllowed: boolean;
  promotionsNavAllowed: boolean;
  priceListsNavAllowed?: boolean;
  loyaltyNavAllowed?: boolean;
  expensesNavAllowed: boolean;
  billingNavAllowed: boolean;
  publicStoreFeaturesAllowed: boolean;
  pharmacyConsultationsAllowed: boolean;
  aiNavAllowed: boolean;
  teamNavAllowed?: boolean;
  effectiveCapabilities?: Set<BusinessCapability>;
  /** Día 1: priorizar Catálogo en tabs aunque haya canal online. */
  day1PreferCatalogTab?: boolean;
  /** Día 1: colapsar zona Tienda online en sidebar. */
  day1CollapseStorefrontNav?: boolean;
  /**
   * Evidencia día 1 lista (fase / preferCatalog).
   * Default true en tests / callers que no pasan el flag.
   */
  day1NavLoaded?: boolean;
  /**
   * Publicación de tienda: null = pending (ocultar zona).
   * Independiente del checklist día 1 — lo hidrata el shell + useDay1Path.
   */
  storePublished?: boolean | null;
};


/** Ítems absorbidos por cada hub — la puerta es visible si alguno lo es. */
const HUB_CHILD_ITEM_IDS: Record<'pricingHub' | 'settingsHub', NavItemId[]> = {
  pricingHub: ['promotions', 'priceLists', 'loyalty'],
  settingsHub: [
    'organization',
    'billing',
    'branchIdentity',
    'branchPosOps',
    'branchDevices',
    'integrationsHub',
  ],
};

function isItemVisible(item: NavigationManifestItem, ctx: NavVisibilityContext): boolean {
  if (item.featureFlag === 'fiado_integrations' && !ctx.customerTabAllowed) return false;
  if (item.featureFlag === 'promotions_integrations' && !ctx.promotionsNavAllowed) return false;
  if (item.featureFlag === 'price_lists_integrations' && !ctx.priceListsNavAllowed) return false;
  if (item.featureFlag === 'loyalty_integrations' && !ctx.loyaltyNavAllowed) return false;
  if (item.featureFlag === 'expenses_module' && !ctx.expensesNavAllowed) return false;
  if (item.featureFlag === 'public_store_features' && !ctx.publicStoreFeaturesAllowed) return false;
  if (item.featureFlag === 'ai_chat_enabled' && !ctx.aiNavAllowed) return false;
  if (item.id === 'billing' && !ctx.billingNavAllowed) return false;
  // allow_employees plan gate; module Team.view still required via allowedModules
  if (item.id === 'team' && ctx.teamNavAllowed === false) return false;
  if (item.id === 'consultations' && !ctx.pharmacyConsultationsAllowed) return false;
  // Pedidos = hub del canal online: solo con tienda publicada. Excepción:
  // agenda sin delivery (se relabela "Bandeja" — turnos/consultas, no pedidos).
  if (
    item.id === 'ordersInbox' &&
    ctx.storePublished !== true &&
    !(hasCap(ctx, 'appointments') && !hasCap(ctx, 'delivery_fulfillment'))
  ) {
    return false;
  }
  // Puertas hub multi-módulo: visibles si al menos un hijo absorbido lo es
  // (cada hijo conserva su propio flag + módulo + capability).
  if (item.id === 'pricingHub' || item.id === 'settingsHub') {
    return HUB_CHILD_ITEM_IDS[item.id].some(
      (childId) => findVisibleItem(childId, ctx) !== undefined,
    );
  }
  if (ctx.effectiveCapabilities && !navItemAllowedByCapabilities(item.id, ctx.effectiveCapabilities)) {
    return false;
  }
  if (!item.module) return true;
  if (ctx.effectiveCapabilities && !moduleAllowedByCapabilities(item.module, ctx.effectiveCapabilities)) {
    return false;
  }
  return ctx.allowedModules.has(item.module);
}

function findVisibleItem(id: NavItemId, ctx: NavVisibilityContext): NavigationManifestItem | undefined {
  const item = NAVIGATION_MANIFEST.find((i) => i.id === id);
  if (!item || !isItemVisible(item, ctx)) return undefined;
  return item;
}

function hasCap(ctx: NavVisibilityContext, cap: BusinessCapability): boolean {
  return ctx.effectiveCapabilities?.has(cap) ?? false;
}

/**
 * Cuántos segmentos puede mostrar OrdersInbox para esta org (espejo de la
 * lógica de OrdersInboxScreen: cada pestaña gated por capability + módulo).
 * Con más de un segmento posible la puerta es «Bandeja»; «Pedidos» solo
 * cuando la única pestaña posible es pedidos online.
 */
function ordersInboxSegmentPotential(ctx: NavVisibilityContext): number {
  const canViewOrders = ctx.allowedModules.has('Orders');
  const canViewStorefront = ctx.allowedModules.has('BranchSettings');
  const canViewAgenda = ctx.allowedModules.has('Agenda');
  let segments = 0;
  if (hasCap(ctx, 'delivery_fulfillment') && canViewOrders) segments += 1;
  if (ctx.publicStoreFeaturesAllowed && canViewStorefront) segments += 2; // Reclamos + Mensajes
  if (ctx.pharmacyConsultationsAllowed && canViewStorefront) segments += 1;
  if (hasCap(ctx, 'service_quotes') && canViewStorefront) segments += 1;
  if (hasCap(ctx, 'appointments') && canViewAgenda) segments += 1;
  return segments;
}

/** Label override when capabilities reshape the nav (e.g. appointments → "Bandeja"). */
export function resolveNavItemLabel(
  item: NavigationManifestItem,
  caps?: Set<BusinessCapability>,
  ctx?: NavVisibilityContext
): string {
  if (item.id === 'ordersInbox') {
    if (caps?.has('appointments') && !caps?.has('delivery_fulfillment')) {
      return 'Bandeja';
    }
    if (ctx && ordersInboxSegmentPotential(ctx) > 1) {
      return 'Bandeja';
    }
  }
  return item.label;
}

function withResolvedNavLabels(
  items: NavigationManifestItem[],
  ctx: NavVisibilityContext
): NavigationManifestItem[] {
  const caps = ctx.effectiveCapabilities;
  return items.map((item) => {
    const label = resolveNavItemLabel(item, caps, ctx);
    return label === item.label ? item : { ...item, label, shortLabel: label };
  });
}

/**
 * Cascada de tabs mobile: Inicio fijo + 3 operativos (+ Más aparte).
 * Con `appointments`: Inicio · POS · Agenda · Catálogo (agenda = centro del día).
 * Sino: Slot A = POS · B = cola del día · C = control (Stock → Clientes → Catálogo → Caja / salón).
 */
export function resolveMobileTabCascade(ctx: NavVisibilityContext): NavigationManifestItem[] {
  const tabs: NavigationManifestItem[] = [];

  const home = findVisibleItem('home', ctx);
  if (home) tabs.push(home);

  const pos = findVisibleItem('pos', ctx);
  if (pos) tabs.push(pos);

  const used = new Set<NavItemId>(tabs.map((t) => t.id));

  const pick = (id: NavItemId): NavigationManifestItem | undefined => {
    if (used.has(id)) return undefined;
    const item = findVisibleItem(id, ctx);
    if (!item) return undefined;
    used.add(id);
    return item;
  };

  // Rubros con turnos: Agenda es el tab central; Catálogo cierra el día operativo.
  if (hasCap(ctx, 'appointments')) {
    const agenda = pick('appointments');
    if (agenda) tabs.push(agenda);
    const catalog = pick('catalog');
    if (catalog) tabs.push(catalog);
    return tabs;
  }

  // Slot B — cola del día (día 1 fresh: Catálogo antes que Pedidos)
  let slotB: NavigationManifestItem | undefined;
  if (ctx.day1PreferCatalogTab) {
    slotB = pick('catalog');
  }
  if (!slotB && ctx.publicStoreFeaturesAllowed) {
    slotB = pick('ordersInbox');
  }
  if (!slotB && hasCap(ctx, 'tables')) {
    slotB = pick('diningTables');
  }
  if (!slotB) {
    slotB = pick('catalog');
  }
  if (slotB) tabs.push(slotB);

  // Slot C — cascada
  let slotC: NavigationManifestItem | undefined;

  if (slotB?.id === 'ordersInbox' && hasCap(ctx, 'tables')) {
    slotC = pick('diningTables') ?? pick('kitchenQueue');
  } else if (slotB?.id === 'diningTables' && hasCap(ctx, 'kitchen_tickets')) {
    slotC = pick('kitchenQueue');
  }

  // Con B=Catálogo (solo local), Clientes gana el slot sobre Stock (matriz doc 03/07).
  // Con B=Pedidos (canal online), Stock tiene prioridad.
  if (!slotC && slotB?.id === 'catalog') {
    slotC = pick('fiado') ?? pick('inventory');
  }
  if (!slotC) slotC = pick('inventory');
  if (!slotC) slotC = pick('fiado');
  if (!slotC && slotB?.id !== 'catalog') slotC = pick('catalog');
  if (!slotC) slotC = pick('cash');
  if (!slotC) slotC = pick('kitchenQueue');
  if (!slotC) slotC = pick('diningTables');

  if (slotC) tabs.push(slotC);

  return tabs;
}

export function getManifestItemsForSurface(
  surface: NavSurface,
  ctx: NavVisibilityContext
): NavigationManifestItem[] {
  if (surface === 'mobileTab') {
    return withResolvedNavLabels(resolveMobileTabCascade(ctx), ctx);
  }

  const tabRoutes = new Set(resolveMobileTabCascade(ctx).map((i) => i.route));

  return withResolvedNavLabels(
    NAVIGATION_MANIFEST.filter((item) => {
      if (!item.surfaces.includes(surface)) return false;
      if (!isItemVisible(item, ctx)) return false;
      // En Más no repetir lo que ya está en la barra de tabs.
      if (surface === 'mobileMore' && tabRoutes.has(item.route)) return false;
      return true;
    }).sort((a, b) => a.order - b.order),
    ctx
  );
}

export function getSidebarSections(ctx: NavVisibilityContext): Array<{
  zone: NavZone;
  label: string;
  items: NavigationManifestItem[];
  /** Día 1: zona colapsada (solo label / hint, sin ítems operativos). */
  collapsed?: boolean;
}> {
  const items = getManifestItemsForSurface('sidebar', ctx);
  // Asegurar Home + POS en sidebar (Inicio primero en Vender).
  const sidebarItems = [...items];
  for (const id of ['home', 'pos'] as const) {
    const item = findVisibleItem(id, ctx);
    if (item && !sidebarItems.some((i) => i.id === id)) {
      sidebarItems.push(item);
    }
  }
  sidebarItems.sort((a, b) => a.order - b.order);
  const labeledItems = withResolvedNavLabels(sidebarItems, ctx);

  const zones: NavZone[] = [
    'sell',
    'control',
    'inventory',
    'pricing',
    'storefront',
    'negocio',
    'account',
  ];
  const day1NavLoaded = ctx.day1NavLoaded !== false;
  const storePublished = ctx.storePublished;
  return zones
    .map((zone) => {
      const zoneItems = labeledItems.filter((i) => i.zone === zone);
      if (zone === 'storefront') {
        // Pending: no sabemos si está publicada → no pintar nada.
        if (storePublished === null || storePublished === undefined) {
          return {
            zone,
            label: NAV_ZONE_LABELS[zone],
            items: [] as NavigationManifestItem[],
            collapsed: undefined,
          };
        }
        // No publicada: CTA si día1 pide colapsar, o si día1 aún no resolvió
        // (nunca mostrar menú operativo “como si tuviera tienda” en ese gap).
        if (storePublished === false) {
          const showCollapseCta =
            Boolean(ctx.day1CollapseStorefrontNav) || !day1NavLoaded;
          if (showCollapseCta && zoneItems.length > 0) {
            return {
              zone,
              label: NAV_ZONE_LABELS[zone],
              items: [] as NavigationManifestItem[],
              collapsed: true,
            };
          }
          // Dueño establecido sin publicar: menú completo para configurar.
          return {
            zone,
            label: NAV_ZONE_LABELS[zone],
            items: zoneItems,
            collapsed: undefined,
          };
        }
        // Publicada: menú completo.
        return {
          zone,
          label: NAV_ZONE_LABELS[zone],
          items: zoneItems,
          collapsed: undefined,
        };
      }
      return {
        zone,
        label: NAV_ZONE_LABELS[zone],
        items: zoneItems,
        collapsed: undefined,
      };
    })
    .filter((s) => s.items.length > 0 || s.collapsed);
}

export function getMobileTabManifestItems(ctx: NavVisibilityContext): NavigationManifestItem[] {
  return getManifestItemsForSurface('mobileTab', ctx);
}

export function getMobileMoreSections(ctx: NavVisibilityContext): Array<{
  zone: NavZone;
  label: string;
  items: NavigationManifestItem[];
}> {
  const items = getManifestItemsForSurface('mobileMore', ctx);
  const zones: NavZone[] = [
    'control',
    'inventory',
    'pricing',
    'sell',
    'storefront',
    'negocio',
    'account',
  ];
  return zones
    .map((zone) => ({
      zone,
      label: NAV_ZONE_LABELS[zone],
      items: items.filter((i) => i.zone === zone),
    }))
    .filter((s) => s.items.length > 0);
}

export function shouldShowMobileMoreTab(ctx: NavVisibilityContext): boolean {
  return getManifestItemsForSurface('mobileMore', ctx).length > 0;
}

/** Rutas registradas en MainTabs (incluye tabs ocultos accesibles desde «Más»). */
export const MAIN_TABS_ROUTE_NAMES = [
  'Home',
  'POS',
  'Catalog',
  'Inventory',
  'Customers',
  'Reports',
  'CashManagement',
  'SalesList',
  'Expenses',
  'Purchases',
  'PromotionsList',
  'PriceListsList',
  'LoyaltyProgram',
  'PricingHub',
  'SupportReports',
  'Profile',
  'OrdersInbox',
  'OnlineOrders',
  'OnlineComplaints',
  'Consultations',
  'Quotes',
  'Appointments',
  'WorkOrders',
  'CommissionsDashboard',
  'MyCommissions',
  'CommissionPlans',
  'BoothRentals',
  'SettlementDetail',
  'DiningTables',
  'KitchenQueue',
  'AiChat',
  'More',
] as const;

export type MainTabsRouteName = (typeof MAIN_TABS_ROUTE_NAMES)[number];

export const MOBILE_MORE_TAB_ROUTE = 'More' as const;

export function isMainTabsRoute(route: string): route is MainTabsRouteName {
  return (MAIN_TABS_ROUTE_NAMES as readonly string[]).includes(route);
}

/**
 * Determina si una ruta del manifiesto debe registrarse como tab oculto en MainTabs.
 * Condiciones: visible en «mobileMore» o «stackOnly» (ruta absorbida por un hub,
 * alcanzable por deep-link/navigate) y no es ya un tab visible.
 */
export function shouldRegisterHiddenTab(
  ctx: NavVisibilityContext,
  route: string
): boolean {
  if (!isMainTabsRoute(route)) return false;
  if (getMobileTabManifestItems(ctx).some((item) => item.route === route)) return false;
  return (
    getManifestItemsForSurface('mobileMore', ctx).some((item) => item.route === route) ||
    getManifestItemsForSurface('stackOnly', ctx).some((item) => item.route === route)
  );
}

/**
 * Tab oculto cuando el dueño apagó una función (override) pero el rubro y permisos siguen teniéndola.
 * Evita desregistrar la ruta mid-session (crash si el foco quedó en ese tab).
 */
export function shouldRegisterOperationModeHiddenTab(
  ctx: NavVisibilityContext,
  route: string,
  rubroCapabilities: Set<BusinessCapability>,
): boolean {
  if (!isMainTabsRoute(route)) return false;
  if (getMobileTabManifestItems(ctx).some((item) => item.route === route)) return false;
  const item = NAVIGATION_MANIFEST.find((entry) => entry.route === route);
  if (!item?.module) return false;
  if (!ctx.allowedModules.has(item.module)) return false;
  const effective = ctx.effectiveCapabilities ?? new Set<BusinessCapability>();
  if (!navItemAllowedByCapabilities(item.id, rubroCapabilities)) return false;
  return !navItemAllowedByCapabilities(item.id, effective);
}

/**
 * Ruta inicial de MainTabs: Inicio si está visible; si no, POS; si no, primer tab.
 */
export function resolveInitialMainTabRoute(ctx: NavVisibilityContext): MainTabsRouteName {
  const visibleTabs = getMobileTabManifestItems(ctx);
  const homeTab = visibleTabs.find((item) => item.route === 'Home');
  if (homeTab && isMainTabsRoute(homeTab.route)) return homeTab.route;

  const posTab = visibleTabs.find((item) => item.route === 'POS');
  if (posTab && isMainTabsRoute(posTab.route)) return posTab.route;

  const first = visibleTabs[0];
  if (first && isMainTabsRoute(first.route)) return first.route;

  if (shouldRegisterHiddenTab(ctx, 'Profile')) return 'Profile';
  if (shouldShowMobileMoreTab(ctx)) return MOBILE_MORE_TAB_ROUTE;

  const moreItems = getManifestItemsForSurface('mobileMore', ctx);
  const firstMore = moreItems.find((item) => isMainTabsRoute(item.route));
  if (firstMore && isMainTabsRoute(firstMore.route)) return firstMore.route;

  return 'Profile';
}

export function buildNavVisibilityContext(params: {
  permissions: Array<{ module: ModuleKey; canView: boolean }>;
  customerTabAllowed: boolean;
  promotionsNavAllowed: boolean;
  priceListsNavAllowed?: boolean;
  loyaltyNavAllowed?: boolean;
  expensesNavAllowed: boolean;
  billingNavAllowed: boolean;
  publicStoreFeaturesAllowed: boolean;
  pharmacyConsultationsAllowed: boolean;
  aiNavAllowed?: boolean;
  teamNavAllowed?: boolean;
  effectiveCapabilities?: Set<BusinessCapability>;
  day1PreferCatalogTab?: boolean;
  day1CollapseStorefrontNav?: boolean;
  day1NavLoaded?: boolean;
  storePublished?: boolean | null;
}): NavVisibilityContext {
  const allowedModules = new Set<ModuleKey>();
  for (const permission of params.permissions) {
    if (permission.canView) allowedModules.add(permission.module);
  }
  return {
    allowedModules,
    customerTabAllowed: params.customerTabAllowed,
    promotionsNavAllowed: params.promotionsNavAllowed,
    priceListsNavAllowed: params.priceListsNavAllowed ?? false,
    loyaltyNavAllowed: params.loyaltyNavAllowed ?? false,
    expensesNavAllowed: params.expensesNavAllowed,
    billingNavAllowed: params.billingNavAllowed,
    publicStoreFeaturesAllowed: params.publicStoreFeaturesAllowed,
    pharmacyConsultationsAllowed: params.pharmacyConsultationsAllowed,
    aiNavAllowed: params.aiNavAllowed ?? false,
    teamNavAllowed: params.teamNavAllowed,
    effectiveCapabilities: params.effectiveCapabilities,
    day1PreferCatalogTab: params.day1PreferCatalogTab ?? false,
    day1CollapseStorefrontNav: params.day1CollapseStorefrontNav ?? false,
    day1NavLoaded: params.day1NavLoaded ?? true,
    // Default true en tests legacy (tienda “ok”); el shell pasa null|boolean real.
    storePublished: params.storePublished === undefined ? true : params.storePublished,
  };
}

export function findManifestItemByRoute(route: string): NavigationManifestItem | undefined {
  return NAVIGATION_MANIFEST.find((i) => i.route === route);
}

/**
 * Destinos de primer nivel del sidebar que viven en ShellStack (no en MainTabs).
 * En PC (≥ breakpoint) no muestran flecha atrás — no son drill-down de Perfil/Org.
 */
export function getShellSidebarDestinationRoutes(): string[] {
  return NAVIGATION_MANIFEST.filter(
    (item) => item.surfaces.includes('sidebar') && !isMainTabsRoute(item.route)
  ).map((item) => item.route);
}

export function isShellSidebarDestination(route: string): boolean {
  const item = findManifestItemByRoute(route);
  return Boolean(item?.surfaces.includes('sidebar') && !isMainTabsRoute(route));
}

/** Sub-rutas ShellStack bajo Perfil — el sidebar resalta «Cuenta». */
export const SHELL_PROFILE_CHILD_ROUTES = new Set([
  'ActivityHistory',
  'Glossary',
]);

/** Sub-rutas ShellStack bajo Equipo — el sidebar resalta «Equipo». */
export const SHELL_TEAM_CHILD_ROUTES = new Set([
  'EmployeePermissions',
  'InviteEmployee',
  'TeamHistory',
]);

/** Sub-rutas ShellStack bajo Organización. */
export const SHELL_ORG_CHILD_ROUTES = new Set([
  'ChangeBusinessType',
  'BusinessFunctions',
  'OperationMode',
  'CapabilityBreakdown',
  'AddFeatures',
]);

/** Sub-rutas ShellStack bajo hubs de sucursal / integraciones. */
export const SHELL_BRANCH_CHILD_ROUTES = new Set([
  'ArcaIntegration',
  'FiscalHub',
  'MercadoPagoBranchSettings',
  'BranchSettings',
  'BranchIdentity',
  'BranchPosOps',
  'BranchDevices',
  'StorefrontHub',
  'DeliverySettings',
  'StoreReviews',
  'StoreReviewDetail',
  'IntegrationsHub',
]);

/**
 * Rutas absorbidas por el hub Configuración (SettingsHub): filas «Tu negocio»
 * y «Este local» + sus drill-downs (funciones, fiscal, Mercado Pago…).
 * El sidebar resalta «Configuración» mientras estés en cualquiera.
 */
export const SHELL_SETTINGS_HUB_CHILD_ROUTES = new Set([
  'Organization',
  'Billing',
  'BranchIdentity',
  'BranchPosOps',
  'BranchDevices',
  'IntegrationsHub',
  'BranchSettings',
  'ChangeBusinessType',
  'BusinessFunctions',
  'OperationMode',
  'CapabilityBreakdown',
  'AddFeatures',
  'ArcaIntegration',
  'FiscalHub',
  'MercadoPagoBranchSettings',
]);

/**
 * Hubs drill-down desde un destino del sidebar (Integraciones → Fiscal / MP / ARCA).
 * En PC el exit es el ítem del sidebar; en mobile/narrow se muestra ← en el header.
 * No incluir detalles reales (SaleDetail, StoreReviewDetail, …).
 */
export const SHELL_SIDEBAR_CHILD_HUB_ROUTES = new Set([
  'ArcaIntegration',
  'FiscalHub',
  'MercadoPagoBranchSettings',
]);

/** Operación diaria (ventas, caja…): sucursal + «Cambiar» en el header, como los tabs. */
export const SHELL_OPERATIONAL_HEADER_ROUTES = new Set([
  'SaleDetail',
]);

/** Módulos anidados (datos maestros, promos, salón): título + sync, sin sucursal en header. */
export const SHELL_COMPACT_HEADER_ROUTES = new Set([
  'DiningTables',
  'Reservations',
  'KitchenQueue',
  'MenuOptionGroups',
  'ProductRecipe',
  'ProduceRecipe',
  'DisassembleRecipe',
  'AppointmentResources',
]);

export function shellHeaderUsesCompactChrome(route: string): boolean {
  return SHELL_COMPACT_HEADER_ROUTES.has(route);
}

const SHELL_ACCOUNT_HEADER_ROOT_ROUTES = new Set([
  'Team',
  'Organization',
  'Billing',
  'SettingsHub',
  'BranchSettings',
  'BranchIdentity',
  'BranchPosOps',
  'BranchDevices',
  'StorefrontHub',
  'DeliverySettings',
  'StoreReviews',
  'IntegrationsHub',
  'SupportTicketDetail',
]);

/** Configuración y soporte: solo título + sync (sin sucursal ni accesos duplicados a Perfil/Soporte). */
export function shellHeaderUsesAccountChrome(route: string): boolean {
  return (
    SHELL_ACCOUNT_HEADER_ROOT_ROUTES.has(route) ||
    SHELL_PROFILE_CHILD_ROUTES.has(route) ||
    SHELL_TEAM_CHILD_ROUTES.has(route) ||
    SHELL_ORG_CHILD_ROUTES.has(route) ||
    SHELL_BRANCH_CHILD_ROUTES.has(route)
  );
}

/** Muestra sucursal y acceso «Cambiar» en el título del header. */
export function shellHeaderShowsBranch(route: string): boolean {
  return SHELL_OPERATIONAL_HEADER_ROUTES.has(route);
}

export function isShellSidebarItemActive(itemRoute: string, activeShellRoute: string): boolean {
  if (activeShellRoute === itemRoute) return true;
  if (itemRoute === 'Profile' && SHELL_PROFILE_CHILD_ROUTES.has(activeShellRoute)) return true;
  if (itemRoute === 'Team' && SHELL_TEAM_CHILD_ROUTES.has(activeShellRoute)) return true;
  // Configuración: puerta activa en cualquier fila hija o drill-down de las mismas.
  if (itemRoute === 'SettingsHub' && SHELL_SETTINGS_HUB_CHILD_ROUTES.has(activeShellRoute)) {
    return true;
  }
  // Tienda online: la puerta queda activa en los segmentos absorbidos
  // (deep-link directo a la ruta ShellStack) y en el detalle de reseña.
  if (
    itemRoute === 'StorefrontHub' &&
    (activeShellRoute === 'DeliverySettings' ||
      activeShellRoute === 'StoreReviews' ||
      activeShellRoute === 'StoreReviewDetail')
  ) {
    return true;
  }
  // Catálogo: la puerta queda activa en los segmentos absorbidos (deep-link
  // directo a la ruta ShellStack) y en Receta/Producir/Fraccionar.
  if (
    itemRoute === 'Catalog' &&
    (activeShellRoute === 'MenuOptionGroups' ||
      activeShellRoute === 'ProductCombos' ||
      activeShellRoute === 'ProductRecipe' ||
      activeShellRoute === 'ProduceRecipe' ||
      activeShellRoute === 'DisassembleRecipe')
  ) {
    return true;
  }
  // Precios: la puerta queda activa en los segmentos absorbidos.
  if (
    itemRoute === 'PricingHub' &&
    (activeShellRoute === 'PromotionsList' ||
      activeShellRoute === 'PriceListsList' ||
      activeShellRoute === 'LoyaltyProgram')
  ) {
    return true;
  }
  if (itemRoute === 'Consultations' && activeShellRoute === 'ConsultationDetail') return true;
  if (itemRoute === 'Quotes' && activeShellRoute === 'QuoteDetail') return true;
  if (itemRoute === 'Appointments' && activeShellRoute === 'AppointmentResources') return true;
  if (
    itemRoute === 'OrdersInbox' &&
    (activeShellRoute === 'OnlineOrders' ||
      activeShellRoute === 'OrdersHistory' ||
      activeShellRoute === 'OnlineComplaints' ||
      activeShellRoute === 'Consultations' ||
      activeShellRoute === 'Quotes' ||
      activeShellRoute === 'OrdersInbox' ||
      activeShellRoute === 'OnlineOrderDetail')
  ) {
    return true;
  }
  if (
    itemRoute === 'CommissionsDashboard' &&
    (activeShellRoute === 'MyCommissions' ||
      activeShellRoute === 'CommissionPlans' ||
      activeShellRoute === 'BoothRentals' ||
      activeShellRoute === 'SettlementDetail')
  ) {
    return true;
  }
  return false;
}
